import { useEffect, useState } from 'react';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { ActionCreators, IEpletGraphDataResponse, IEpletGraphDataResult } from '../actions-toolbox';

interface IOptions {
    cache?: boolean
}

interface IRetVal {
    data: IEpletGraphDataResult,
    loading: boolean,
    error: string
}

const cachedResults: { [recipientId: number]: IEpletGraphDataResult } = {};

function useEpletGraphData(recipientId: number, options: IOptions = {}): IRetVal {
    const { dispatch } = useAppHookContext();
    const [data, setData] = useState<IEpletGraphDataResult>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        setError('');

        if (!recipientId) {
            setData(null);
            return;
        }

        if (options.cache && cachedResults[recipientId]) {
            setData(cachedResults[recipientId]);
            return;
        }

        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);
            setData(null);

            try {
                const response: IEpletGraphDataResponse = await dispatch(ActionCreators.getEpletGraphData(recipientId));

                if (cancelled) return;

                if (response && response.data.success && response.data.results) {
                    cachedResults[recipientId] = response.data.results;
                    setData(response.data.results);
                } else {
                    setError('Unable to load eplet graph data');
                }
            } catch (err) {
                if (!cancelled) setError('Unable to load eplet graph data');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();

        return () => { cancelled = true; };
    }, [recipientId, options.cache]);

    return { data, loading, error };
}

export default useEpletGraphData;
